import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'shared/components/Button/Button';
import AccountGraphic from 'shared/components/icons/AccountGraphic';
import { ArrowRightIcon } from 'shared/components/icons/ArrowRightIcon';
import { ArrowFromBracketIcon } from 'shared/components/icons/ArrowFromBracketIcon';
import { MilestoneType } from 'modules/types';
import { useUser } from 'hooks/useUser';
import { AgreementType, FlagKey, OnboardingMilestone } from 'api/types';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { getDashboardRedirectPath } from 'modules/auth/Auth.utils';
import { useNavigate } from 'react-router';
import { useGlobalState } from 'hooks/useGlobalState';
import {
  AccountBodyContainer,
  AccountBodyHeader,
  AgreementNumber,
  ItemButton,
  ListText,
  LogoutWrapper,
  Name,
  NameWrapper,
  TextContainer,
} from './AccountBody.styles';
import { AddressCard } from '../AddressCard/AddressCard';

type Props = {
  name: string;
  agreementNumber: string;
  items: { title: string; icon: JSX.Element; onClick: () => void }[];
  onUnImpersonateUser?: () => void;
  onLogout: () => void;
};

export function AccountBody({
  name,
  agreementNumber,
  items,
  onUnImpersonateUser,
  onLogout,
}: Props) {
  const { t } = useTranslation();
  const { userResult, selectedAgreement, setSelectedAgreement } = useUser();
  const navigate = useNavigate();
  const { setAccountMenuOpen } = useGlobalState();
  const {
    flags: { [FlagKey.onboardingRefresh]: onboardingRefresh },
  } = useFeatureFlags();
  const user = userResult?.user;

  const handleSelectAgreement = useCallback(
    (agreement: AgreementType) => {
      const { hasDashboardAccess, redirectPath } = getDashboardRedirectPath(
        user,
        agreement,
        onboardingRefresh,
      );
      setSelectedAgreement(agreement);
      if (!hasDashboardAccess) {
        navigate(redirectPath);
      }
      setAccountMenuOpen(false);
    },
    [user, setSelectedAgreement, onboardingRefresh, navigate, setAccountMenuOpen],
  );

  const addressCards = useMemo(() => {
    const agreements = user?.agreements;
    if (agreements && agreements.length > 1) {
      return agreements.map((agreement) => {
        const {
          id,
          current_milestone: currentMilestone,
          onboarding_milestone: onboardingMilestone,
          system: { address },
        } = agreement;
        const addressString = t('address', {
          street_1: address.street_1,
          street_2: address.street_2 ? ` ${address.street_2}` : '',
          city: address.city,
        });
        return (
          <AddressCard
            key={id}
            address={addressString}
            isSelected={id === selectedAgreement?.id}
            actionRequired={
              currentMilestone !== MilestoneType.IN_SERVICE &&
              onboardingMilestone !== OnboardingMilestone.ONBOARDING_COMPLETE
            }
            onSelectAddress={() => handleSelectAgreement(agreement)}
          />
        );
      });
    }
    return null;
  }, [user?.agreements, selectedAgreement, t, handleSelectAgreement]);

  return (
    <AccountBodyContainer>
      <AccountBodyHeader>
        <AccountGraphic />
        <NameWrapper>
          <Name as="h3">{name}</Name>
          {addressCards || <AgreementNumber as="p">{agreementNumber}</AgreementNumber>}
        </NameWrapper>
      </AccountBodyHeader>

      {items.map(({ title, icon, onClick }) => (
        <ItemButton key={title} onClick={onClick}>
          {icon}
          <TextContainer>
            <ListText as="p">{title}</ListText>
            <ArrowRightIcon />
          </TextContainer>
        </ItemButton>
      ))}

      <LogoutWrapper>
        {onUnImpersonateUser && (
          <Button
            styleVariant="tertiary"
            label={t('account.stopImpersonation')}
            iconRight
            isFullWidth
            onClick={onUnImpersonateUser}
          />
        )}

        <Button
          styleVariant="secondary"
          label={t('logoutModal.logout')}
          Icon={ArrowFromBracketIcon}
          iconRight
          isFullWidth
          onClick={onLogout}
        />
      </LogoutWrapper>
    </AccountBodyContainer>
  );
}

export default AccountBody;
